/**
 * i18n Configuration - Internationalization setup for the application.
 *
 * Supports: pt-BR (default), en, es
 * Uses: react-i18next with lazy-loaded namespaced translations per feature.
 * Persistence: Language preference saved to localStorage.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// === Locale imports: pt-BR ===
import commonPtBR from "@/locales/pt-BR/common.json";
import sidebarPtBR from "@/locales/pt-BR/sidebar.json";
import chatPtBR from "@/locales/pt-BR/chat.json";
import projectPtBR from "@/locales/pt-BR/project.json";
import sessionPtBR from "@/locales/pt-BR/session.json";
import excelPtBR from "@/locales/pt-BR/excel.json";
import adminPtBR from "@/locales/pt-BR/admin.json";
import authPtBR from "@/locales/pt-BR/auth.json";
import errorsPtBR from "@/locales/pt-BR/errors.json";
import memoryPtBR from "@/locales/pt-BR/memory.json";
import ragPtBR from "@/locales/pt-BR/rag.json";
import drePtBR from "@/locales/pt-BR/dre.json";
import sharedPtBR from "@/locales/pt-BR/shared.json";
import bugReportsPtBR from "@/locales/pt-BR/bugReports.json";

// === Locale imports: en ===
import commonEn from "@/locales/en/common.json";
import sidebarEn from "@/locales/en/sidebar.json";
import chatEn from "@/locales/en/chat.json";
import projectEn from "@/locales/en/project.json";
import sessionEn from "@/locales/en/session.json";
import excelEn from "@/locales/en/excel.json";
import adminEn from "@/locales/en/admin.json";
import authEn from "@/locales/en/auth.json";
import errorsEn from "@/locales/en/errors.json";
import memoryEn from "@/locales/en/memory.json";
import ragEn from "@/locales/en/rag.json";
import dreEn from "@/locales/en/dre.json";
import sharedEn from "@/locales/en/shared.json";
import bugReportsEn from "@/locales/en/bugReports.json";

// === Locale imports: es ===
import commonEs from "@/locales/es/common.json";
import sidebarEs from "@/locales/es/sidebar.json";
import chatEs from "@/locales/es/chat.json";
import projectEs from "@/locales/es/project.json";
import sessionEs from "@/locales/es/session.json";
import excelEs from "@/locales/es/excel.json";
import adminEs from "@/locales/es/admin.json";
import authEs from "@/locales/es/auth.json";
import errorsEs from "@/locales/es/errors.json";
import memoryEs from "@/locales/es/memory.json";
import ragEs from "@/locales/es/rag.json";
import dreEs from "@/locales/es/dre.json";
import sharedEs from "@/locales/es/shared.json";
import bugReportsEs from "@/locales/es/bugReports.json";

export const SUPPORTED_LANGUAGES = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: SupportedLanguage = "pt-BR";

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            "pt-BR": {
                common: commonPtBR,
                sidebar: sidebarPtBR,
                chat: chatPtBR,
                project: projectPtBR,
                session: sessionPtBR,
                excel: excelPtBR,
                admin: adminPtBR,
                auth: authPtBR,
                errors: errorsPtBR,
                memory: memoryPtBR,
                rag: ragPtBR,
                dre: drePtBR,
                shared: sharedPtBR,
                bugReports: bugReportsPtBR,
            },
            en: {
                common: commonEn,
                sidebar: sidebarEn,
                chat: chatEn,
                project: projectEn,
                session: sessionEn,
                excel: excelEn,
                admin: adminEn,
                auth: authEn,
                errors: errorsEn,
                memory: memoryEn,
                rag: ragEn,
                dre: dreEn,
                shared: sharedEn,
                bugReports: bugReportsEn,
            },
            es: {
                common: commonEs,
                sidebar: sidebarEs,
                chat: chatEs,
                project: projectEs,
                session: sessionEs,
                excel: excelEs,
                admin: adminEs,
                auth: authEs,
                errors: errorsEs,
                memory: memoryEs,
                rag: ragEs,
                dre: dreEs,
                shared: sharedEs,
                bugReports: bugReportsEs,
            },
        },
        fallbackLng: DEFAULT_LANGUAGE,
        defaultNS: "common",
        ns: ["common", "sidebar", "chat", "project", "session", "excel", "admin", "auth", "errors", "memory", "rag", "dre", "shared", "bugReports"],
        interpolation: {
            escapeValue: false, // React already handles XSS
        },
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: "i18n_language",
            caches: ["localStorage"],
        },
    });

export default i18n;
